/**
 * Avisar as OUTRAS abas que um dado mudou.
 *
 * O React Query tem um cache por aba. Isso nunca incomodou enquanto tudo
 * acontecia numa aba só — e passou a incomodar no dia em que o editor de
 * plantas ganhou aba própria: salvar ali invalidava o cache DAQUELA aba, e a
 * aba do dashboard continuava desenhando a planta velha. Do lado de quem
 * usa, isso se lê como "salvou e sumiu", e a saída é F5.
 *
 * Piora porque `refetchOnWindowFocus` está desligado por padrão neste app —
 * decisão certa (um dashboard que recarrega a cada alt-tab é um dashboard
 * que pisca), mas ela fecha a única porta pela qual a aba antiga
 * descobriria sozinha.
 *
 * `BroadcastChannel` resolve no lugar certo: quem mudou avisa, quem está
 * aberto recarrega o que foi avisado. Não é sincronização de estado — é um
 * recado dizendo "esta chave envelheceu".
 *
 * `refetchQueries`, e não `invalidateQueries`: invalidar só marca como
 * velho, e consulta sem observador ativo pode ficar marcada sem nunca
 * recarregar. A aba do outro lado pode estar com a tela montada e ociosa,
 * que é exatamente o caso aqui.
 */

import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";

const CANAL = "envpulse:dados";

interface Recado {
  chaves: unknown[][];
}

/** Avisa as outras abas que estas chaves mudaram. */
export function avisarMudanca(chaves: unknown[][]) {
  try {
    const canal = new BroadcastChannel(CANAL);
    canal.postMessage({ chaves } satisfies Recado);
    canal.close();
  } catch {
    // Navegador sem BroadcastChannel (Safari antigo) perde o aviso e
    // continua funcionando como antes. Recado perdido não pode derrubar a
    // gravação que acabou de dar certo.
  }
}

/** Escuta os avisos das outras abas. Montado uma vez, no topo do app. */
export function useSincronizarEntreAbas(qc: QueryClient) {
  useEffect(() => {
    let canal: BroadcastChannel;
    try {
      canal = new BroadcastChannel(CANAL);
    } catch {
      return;
    }
    canal.onmessage = (ev: MessageEvent<Recado>) => {
      for (const chave of ev.data?.chaves ?? []) {
        void qc.refetchQueries({ queryKey: chave });
      }
    };
    return () => canal.close();
  }, [qc]);
}
